// src/components/programs/ProgramForm/helpers/quotationCalculations.js

/**
 * Calculate quantity based on pricing unit and dynamic fields
 */
export const calculateQuantity = (product, paxTypes) => {
    const pricingCode = product.product_snapshot?.pricing_unit;
    const totalPax = Array.isArray(paxTypes)
        ? paxTypes.reduce((sum, pt) => sum + (pt.count || 0), 0)
        : (typeof paxTypes === 'number' ? paxTypes : 0);

    if (product.merged_quantities) {
        if (pricingCode === "per_person") {
            return product.merged_quantities.persons_count * totalPax;
        } else if (pricingCode === "per_night") {
            return product.merged_quantities.nights;
        } else if (pricingCode === "per_unit") {
            return product.merged_quantities.units;
        }
    }

    if (pricingCode === "per_person") {
        return totalPax;
    } else if (pricingCode === "per_night") {
        return product.dynamic_fields?.nights || 0;
    } else if (pricingCode === "per_unit") {
        return product.dynamic_fields?.units || 0;
    }

    return 0;
};

/**
 * Calculate product final price after discount
 */
export const calculateProductFinalPrice = (basePrice, product) => {
    const discountType = product.pricing?.discount_type || "zero";
    const discountValue = product.pricing?.discount_value || 0;

    if (discountType === "zero" || !discountValue) {
        return basePrice;
    }

    if (discountType === "amount") {
        return Math.max(0, basePrice - discountValue);
    }

    if (discountType === "percent") {
        return basePrice - (basePrice * discountValue / 100);
    }

    return basePrice;
};

/**
 * Calculate product totals (price and cost)
 */
export const calculateProductTotals = (product, paxTypes) => {
    const quantity = calculateQuantity(product, paxTypes);
    const pricingCode = product.product_snapshot?.pricing_unit;
    const isPerPerson = pricingCode === "per_person";
    const paxTypesArray = Array.isArray(paxTypes) ? paxTypes : [];

    let totalPrice = 0;
    let totalCost = 0;
    let avgPricePerUnit = 0;
    let avgCostPerUnit = 0;

    const isPriceEdited = product.pricing?.is_price_edited || false;
    const customPrice = parseFloat(product.pricing?.custom_price);
    const hasGlobalCustomPrice = isPriceEdited && !isNaN(customPrice) && customPrice >= 0;

    // Check if supplier has provided pax_type_prices
    const paxPrices = product.product_snapshot?.pax_type_prices || product.pax_type_prices || [];
    const hasPaxPrices = isPerPerson && paxPrices.length > 0;

    if (isPerPerson && hasPaxPrices) {
        let actualItemsCalculated = 0;

        paxTypesArray.forEach(pt => {
            const count = pt.count || 0;
            if (count > 0) {
                const paxConfig = paxPrices.find(p => String(p.pax_type_id) === String(pt.pax_type_id));

                // ✅ Check for individual pax type price override
                const customPaxPrice = parseFloat(product.pricing?.custom_pax_prices?.[pt.pax_type_id]);
                const hasCustomPaxPrice = isPriceEdited && !isNaN(customPaxPrice) && customPaxPrice >= 0;

                // const basePrice = hasCustomPaxPrice 
                //     ? customPaxPrice 
                //     : (parseFloat(paxConfig?.price) || 0);

                // // ✅ NEW: Check for individual pax type cost override
                // const customPaxCost = product.pricing?.custom_pax_costs?.[pt.pax_type_id];
                // const hasCustomPaxCost = customPaxCost !== undefined && customPaxCost !== null && !isNaN(parseFloat(customPaxCost));

                // const baseCost = hasCustomPaxCost
                //     ? parseFloat(customPaxCost)
                //     : (parseFloat(paxConfig?.cost_price) || parseFloat(product.pricing?.cost_price) || 0);
                const paxPriceVal = parseFloat(paxConfig?.price);
                const basePrice = hasCustomPaxPrice
                    ? customPaxPrice
                    : (isNaN(paxPriceVal) ? 0 : paxPriceVal);

                // ✅ NEW: Check for individual pax type cost override
                const customPaxCost = product.pricing?.custom_pax_costs?.[pt.pax_type_id];
                const hasCustomPaxCost = customPaxCost !== undefined && customPaxCost !== null && !isNaN(parseFloat(customPaxCost));

                // ✅ Skip pax type if paxConfig is falsy and there are no custom overrides
                if (!paxConfig && !hasCustomPaxPrice && !hasCustomPaxCost) { return; }

                const paxCostVal = parseFloat(paxConfig?.cost_price);
                const defaultCost = isNaN(paxCostVal) ? (parseFloat(product.pricing?.cost_price) || 0) : paxCostVal;
                const baseCost = hasCustomPaxCost
                    ? parseFloat(customPaxCost)
                    : defaultCost;

                const finalPrice = calculateProductFinalPrice(basePrice, product);

                totalPrice += finalPrice * count;
                totalCost += baseCost * count;
                actualItemsCalculated += count;
            }
        });

        avgPricePerUnit = actualItemsCalculated > 0 ? totalPrice / actualItemsCalculated : 0;
        avgCostPerUnit = actualItemsCalculated > 0 ? totalCost / actualItemsCalculated : 0;
    } else {
        // Legacy or non per-person logic
        const basePrice = hasGlobalCustomPrice ? customPrice : (parseFloat(product.pricing?.price) || 0);

        // ✅ FIX: Check custom_cost_price for non-per-person products
        const customCostPriceVal = parseFloat(product.pricing?.custom_cost_price);
        const hasCustomCostPrice = isPriceEdited && !isNaN(customCostPriceVal) && customCostPriceVal >= 0;
        const baseCost = hasCustomCostPrice ? customCostPriceVal : (parseFloat(product.pricing?.cost_price) || 0);

        const finalPrice = calculateProductFinalPrice(basePrice, product);

        totalPrice = finalPrice * quantity;
        totalCost = baseCost * quantity;

        avgPricePerUnit = finalPrice;
        avgCostPerUnit = baseCost;
    }

    return {
        quantity,
        final_price_per_unit: avgPricePerUnit,
        final_cost_per_unit: avgCostPerUnit,
        total_price: totalPrice,
        total_cost: totalCost,
    };
};

/**
 * Calculate day totals
 */
export const calculateDayTotals = (day, paxTypes) => {
    let totalPrice = 0;
    let totalCost = 0;

    day.products?.forEach((product) => {
        const productTotals = calculateProductTotals(product, paxTypes);
        totalPrice += productTotals.total_price;
        totalCost += productTotals.total_cost;
    });

    return {
        total_price: totalPrice,
        total_cost: totalCost,
        profit: totalPrice - totalCost,
    };
};

/**
 * ✅ Apply global discount with target support (sell, cost, both)
 */
export const applyGlobalDiscount = (subtotalPrice, subtotalCost, globalDiscount) => {
    const discountType = globalDiscount?.type || "zero";
    const discountValue = globalDiscount?.value || 0;
    // ✅ استخدام target بدلاً من category
    const target = globalDiscount?.target || "sell";

    // ✅ إذا كان النوع "zero" أو القيمة 0 أو target "none"، لا يوجد خصم
    if (discountType === "zero" || !discountValue || target === "none") {
        return {
            discount_on_selling: 0,
            discount_on_cost: 0,
            final_selling_price: subtotalPrice,
            final_cost_price: subtotalCost,
        };
    }

    let discountOnSelling = 0;
    let discountOnCost = 0;

    const calculateDiscount = (amount) => {
        if (discountType === "amount") {
            return discountValue;
        } else if (discountType === "percent") {
            return amount * discountValue / 100;
        }
        return 0;
    };

    // ✅ تطبيق الخصم حسب target
    if (target === "sell" || target === "both") {
        discountOnSelling = calculateDiscount(subtotalPrice);
    }

    if (target === "cost" || target === "both") {
        discountOnCost = calculateDiscount(subtotalCost);
    }

    return {
        discount_on_selling: discountOnSelling,
        discount_on_cost: discountOnCost,
        final_selling_price: Math.max(0, subtotalPrice - discountOnSelling),
        final_cost_price: Math.max(0, subtotalCost - discountOnCost),
    };
};

/**
 * Calculate program totals with detailed breakdown
 */
export const calculateProgramTotals = (formData) => {
    const paxTypesInfo = formData.pax_types || [];
    const totalPax = paxTypesInfo.reduce((sum, pt) => sum + (pt.count || 0), 0) || 0;

    let subtotalPrice = 0;
    let subtotalCost = 0;

    formData.itinerary?.forEach((day) => {
        const dayTotals = calculateDayTotals(day, paxTypesInfo);
        subtotalPrice += dayTotals.total_price;
        subtotalCost += dayTotals.total_cost;
    });

    const discountResult = applyGlobalDiscount(
        subtotalPrice,
        subtotalCost,
        formData.global_discount
    );

    const finalSellingPrice = discountResult.final_selling_price;
    const finalCostPrice = discountResult.final_cost_price;
    const profit = finalSellingPrice - finalCostPrice;
    const profitMargin = finalSellingPrice > 0 ? (profit / finalSellingPrice) * 100 : 0;

    return {
        total_pax: totalPax,
        subtotal_selling_price: subtotalPrice,
        subtotal_cost_price: subtotalCost,
        subtotal_profit: subtotalPrice - subtotalCost,
        discount_on_selling: discountResult.discount_on_selling,
        discount_on_cost: discountResult.discount_on_cost,
        total_discount: discountResult.discount_on_selling + discountResult.discount_on_cost,
        final_selling_price: finalSellingPrice,
        final_cost_price: finalCostPrice,
        final_profit: profit,
        profit_margin: profitMargin,
        price_per_person: totalPax > 0 ? finalSellingPrice / totalPax : 0,
        cost_per_person: totalPax > 0 ? finalCostPrice / totalPax : 0,
    };
};

/**
 * ✅ Format currency - Remove .00 if not needed
 */
export const formatCurrency = (amount) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;

    if (isNaN(num)) return '0';

    return num.toLocaleString('en-US', {
        maximumFractionDigits: 2,
    });
};


/**
 * ✅ Format number - Remove .00 if not needed
 */
export const formatNumber = (number, decimals = 2) => {
    const num = typeof number === 'string' ? parseFloat(number) : number;

    if (isNaN(num)) return '0';

    return num.toLocaleString('en-US', {
        maximumFractionDigits: decimals,
    });
};

/**
 * ✅ Format simple price - Remove .00 if not needed
 */
export const formatPrice = (amount) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;

    if (isNaN(num)) return '0';

    return num.toLocaleString('en-US', {
        maximumFractionDigits: 2,
    });
};
